import type { RootState } from '@/store';

import { USER_LOAN_PROGRAM } from '@/config/constants';
import type { CalculateClosingDisclosureProps } from '.';
import { calculateEnhancedInterestRate } from './rateIntegration';

const calculateHELOCInterestRate = (
  baseRate: number,
  creditScore: number,
  ltv: number,
  reduxState?: RootState
) => {
  // HELOC rates are typically variable and based on prime rate
  if (reduxState) {
    return calculateEnhancedInterestRate(
      reduxState,
      'HELOC',
      baseRate,
      creditScore,
      ltv
    );
  }
  return baseRate;
};

export const calculateHELOCClosingDisclosure = (
  props: CalculateClosingDisclosureProps & Record<string, any>,
  reduxState?: RootState
) => {
  const {
    purchasePrice,
    downPayment,
    creditScore,
    propertyType,
    occupancy,
    state: propertyState,
    county,
    city,
    zipCode,
    propertyTaxRate,
    homeownersInsuranceRate,
    hoaFees,
    currentLoanBalance,
    requestedCreditLine,
    marketInterestRate = 7.5, // HELOC rates are typically variable
  } = props as any;

  // HELOC specific calculations
  const helocCreditLine = requestedCreditLine || 100000; // Default $100k line
  const loanAmount = purchasePrice - (downPayment || 0);
  const ltv = purchasePrice ? loanAmount / purchasePrice : 0.8;
  const helocInterestRate = calculateHELOCInterestRate(
    marketInterestRate || 7.5,
    Number(creditScore) || 700,
    Number(ltv) || 0.8,
    reduxState
  );

  // HELOC terms
  const drawPeriod = 120; // 10 year draw period
  const repaymentPeriod = 240; // 20 year repayment period
  const totalTerm = drawPeriod + repaymentPeriod;

  // HELOC specific fees (typically lower than traditional refinance)
  const appraisalFeeBase = 500; // Lower appraisal fee for HELOC
  const processingFeeBase = 500; // Lower processing fee
  const underwritingFee = 300; // Lower underwriting fee
  const applicationFeeBase = 100; // Lower application fee

  // HELOC specific fees
  const annualFee = 50; // Annual maintenance fee
  const inactivityFee = 25; // Fee if no activity for 12 months

  // Title and settlement fees
  const titleSettlementAgentFee = 300; // Lower for HELOC
  const titleLendersTitleInsuranceBase = (helocCreditLine * 0.003).toFixed(2); // Lower rate for HELOC

  // Recording fees
  const countyRecordingFees = 50;
  const stateRecordingFees = 50;
  const recordingTitle = 50;
  const recordingMortgage = 150;
  const recordingDeedBase = 50;

  // Transfer taxes (typically not applicable for HELOC)
  const transferTaxes = 0;

  // Prepaid costs (minimal for HELOC)
  const prepaidsPropertyTaxes = 0; // No prepaid taxes for HELOC
  const prepaidsHomeownersInsurance = 0; // No prepaid insurance for HELOC
  const prepaidInterest = 0; // No prepaid interest for HELOC

  // Monthly payment calculation (interest-only during draw period)
  const monthlyPayment = 0; // No payment required if no balance drawn
  const monthlyPrincipalInterest = 0; // No payment required if no balance drawn

  // Lender cost totals
  const lenderCostTotals =
    appraisalFeeBase +
    processingFeeBase +
    underwritingFee +
    applicationFeeBase +
    annualFee +
    titleSettlementAgentFee +
    parseFloat(titleLendersTitleInsuranceBase) +
    countyRecordingFees +
    stateRecordingFees +
    recordingTitle +
    recordingMortgage +
    recordingDeedBase +
    transferTaxes;

  // Prepaid totals
  const prepaidTotals =
    prepaidsPropertyTaxes + prepaidsHomeownersInsurance + prepaidInterest;

  // Total closing costs
  const totalClosingCosts = lenderCostTotals + prepaidTotals;

  // Cash to close (minimal for HELOC)
  const cashToClose = totalClosingCosts;

  return {
    type: USER_LOAN_PROGRAM.HELOC,
    loanAmount: helocCreditLine,
    interestRate: helocInterestRate,
    monthlyPayment,
    monthlyPrincipalInterest,
    loanTerm: totalTerm,
    drawPeriod,
    repaymentPeriod,
    appraisalFeeBase,
    processingFeeBase,
    underwritingFee,
    applicationFeeBase,
    annualFee,
    inactivityFee,
    titleSettlementAgentFee,
    titleLendersTitleInsuranceBase,
    countyRecordingFees,
    stateRecordingFees,
    recordingTitle,
    recordingMortgage,
    recordingDeedBase,
    transferTaxes,
    prepaidsPropertyTaxes,
    prepaidsHomeownersInsurance,
    prepaidInterest,
    lenderCostTotals,
    prepaidTotals,
    totalClosingCosts,
    cashToClose,
    // HELOC specific properties
    minimumOwnersTitle: 1650,
    maximumOwnersTitle: 5850,
    optionalOwnersTitleInsurance: Math.min(
      Math.max(purchasePrice * 0.00575, 1650),
      5850
    ),
    minimumInspectionFee: 650,
    maximumInspectionFee: 4500,
    homeInspectionFee: Math.min(Math.max(500, 650), 4500),
    // Refinance specific properties
    currentLoanBalance,
    requestedCreditLine: helocCreditLine,
  };
};
